import { init, track, Types } from '@amplitude/analytics-browser'
import { useState, useEffect, ReactNode } from 'react'
import { useLocation } from 'react-router'

import AmplitudeContext from './AmplitudeContext'
import { AMPLITUDE_BUSINESS_ROUTES } from './constants'
import { defaultValue } from './data'

type AmplitudeContextProviderProps = {
    children: ReactNode
}

const AmplitudeContextProvider = ({ children }: AmplitudeContextProviderProps) => {
    const appMode = import.meta.env.VITE_APP_MODE
    const apiKey = import.meta.env.VITE_APP_AMPLITUDE_API_KEY
    const businessApiKey = import.meta.env.VITE_APP_AMPLITUDE_BUSINESS_API_KEY

    const location = useLocation()

    const _apiKey = AMPLITUDE_BUSINESS_ROUTES.some(route => location.pathname.startsWith(route))
        ? businessApiKey
        : apiKey

    const [isInitialized, setIsInitialized] = useState(false)

    useEffect(() => {
        if (appMode === 'production') {
            init(_apiKey, undefined, {
                defaultTracking: {
                    attribution: true,
                    pageViews: false,
                    sessions: false,
                    formInteractions: false,
                    fileDownloads: false,
                },
            })
            setIsInitialized(true)
        }
    }, [_apiKey, appMode])

    function trackAmplitudeEvent(
        eventInput: string | Types.BaseEvent,
        eventProperties?: Record<string, unknown>,
        eventOptions?: Types.EventOptions | undefined
    ): (Types.AmplitudeReturn<Types.Result> | { promise: Promise<{ success: boolean }> }) {
        if (isInitialized) {
            return track(eventInput, eventProperties, eventOptions)
        }
        return defaultValue.trackAmplitudeEvent(eventInput, eventProperties, eventOptions)
    }

    const value = { trackAmplitudeEvent }
    
    return (
        <AmplitudeContext.Provider value={ value }>
            { children }
        </AmplitudeContext.Provider>
    )
}

export default AmplitudeContextProvider
